<template>
  <Card class="mb-0 h-full min-h-[300px] gap-2 py-3" data-testid="dashboard-meetings-card">
    <div class="border-b border-gray-200 px-4 pb-2">
      <div class="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div class="flex items-start justify-between gap-2 sm:contents">
          <div class="space-y-1">
            <div class="flex items-center gap-2">
              <CardTitle>Meetings</CardTitle>
              <Badge variant="gray">{{ meetings.length }}</Badge>
            </div>
            <p class="text-xs text-slate-500">Team-wide schedule for {{ selectedDateLabel }}</p>
          </div>
          <Button as-child size="sm" variant="card-header-action" class="sm:order-last">
            <router-link to="/contacts/calendar">Calendar</router-link>
          </Button>
        </div>

        <DatePicker
          :model-value="selectedDate"
          show-today-relative
          show-tomorrow-relative
          show-yesterday-relative
          class="w-full sm:w-[180px]"
          @update:model-value="emit('update:selectedDate', $event)"
        />
      </div>
    </div>

    <CardContent class="flex flex-1 flex-col px-0">
      <div v-if="meetings.length === 0" class="flex flex-1 items-center justify-center px-6 py-6">
        <div class="flex max-w-sm flex-col items-center text-center">
          <div class="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-500">
            <CalendarDays class="h-5 w-5" />
          </div>
          <p class="mt-4 text-sm font-medium text-slate-900">No meetings scheduled</p>
          <p class="mt-1 text-sm text-slate-500">Nothing is on the calendar for {{ selectedDateLabel }}.</p>
          <Button as-child size="sm" variant="outline" class="mt-4">
            <router-link to="/contacts/calendar">Open team calendar</router-link>
          </Button>
        </div>
      </div>

      <div v-if="meetings.length > 0" class="max-h-80 flex-1 overflow-y-auto">
        <div
          v-for="meeting in sortedMeetings"
          :key="meeting.contact_event_id"
          class="border-b border-gray-200 last:border-b-0"
        >
          <button
            type="button"
            class="flex w-full items-center justify-between gap-4 px-4 py-2.5 text-left transition-colors hover:bg-slate-50"
            :class="{ 'opacity-50': meetingUsher.isPastEvent(usherArgs(meeting)) }"
            @click="meetingUsher.navigateToMeeting(usherArgs(meeting))"
          >
            <div class="min-w-0 space-y-1">
              <p class="truncate text-sm font-medium text-slate-900">
                {{ meeting.name }}
              </p>
              <p class="flex flex-wrap items-center gap-x-1.5 text-xs text-slate-500">
                <span :class="{ 'line-through': meetingUsher.isPastEvent(usherArgs(meeting)) }">
                  {{ formatDate(meeting.start_time, 'h:mm a') }} - {{ formatDate(meeting.end_time, 'h:mm a') }}
                </span>
                <span v-if="meeting.interviewer_name" class="flex items-center gap-1">
                  <span aria-hidden="true">·</span>
                  <User class="h-3 w-3" />
                  {{ meeting.interviewer_name }}
                </span>
              </p>
            </div>

            <span class="flex shrink-0 items-center gap-1 text-xs font-medium text-slate-500">
              {{ meetingUsher.actionTitle(usherArgs(meeting)) }}
              <ChevronRight class="h-3.5 w-3.5" />
            </span>
          </button>
        </div>
      </div>
    </CardContent>
  </Card>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { DashboardMeeting } from '@/api/dashboard';
import { useMeetingUsher, type MeetingDashboardArgs } from '@/composables/useMeetingUsher';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardTitle } from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date_picker';
import DateUtility from '@/utilities/dates';
import { CalendarDays, ChevronRight, User } from 'lucide-vue-next';

const props = defineProps<{
  meetings: DashboardMeeting[];
  selectedDate: string;
}>();

const emit = defineEmits<{
  'update:selectedDate': [value: string];
}>();

const { formatDate } = DateUtility;
const meetingUsher = useMeetingUsher();

const selectedDateLabel = computed(() => formatDate(props.selectedDate, 'MMMM d, yyyy'));

const usherArgs = (meeting: DashboardMeeting): MeetingDashboardArgs => ({
  id: meeting.contact_event_id,
  event_type: meeting.event_type,
  start_time: meeting.start_time,
  end_time: meeting.end_time,
  status: meeting.status,
  location: meeting.location,
});

const sortedMeetings = computed(() => {
  const upcoming = props.meetings.filter(m => !meetingUsher.isPastEvent(usherArgs(m)));
  const completed = props.meetings.filter(m => meetingUsher.isPastEvent(usherArgs(m)));
  return [...upcoming, ...completed];
});
</script>
